/**
 * File upload and validation utilities
 *
 * Provides functions for handling image uploads, validation, and size optimization
 * with support for healthcare-specific requirements.
 *
 * Now uses shared constants from @ctrl-enter/tools package.
 */

import {
  calculateAttachmentsSize,
  getBase64SizeKB,
  ATTACHMENT_LIMITS,
  validateFile as sharedValidateFile,
  validateImageFile as sharedValidateImageFile,
  SUPPORTED_FILE_ACCEPT,
  SUPPORTED_FILE_TYPES,
  SUPPORTED_MIME_TYPES,
} from '@ctrl-enter/tools';
import type { DraftAttachment } from '@ctrl-enter/types';
import { estimateAttachmentTokens } from './token-estimation';
import { randomUUID } from '@ctrl-enter/tools/utils';

// Legacy exports for compatibility
export const SUPPORTED_IMAGE_FORMATS = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
  'image/gif': ['.gif'], // Note: Only first frame is processed by OpenAI
} as const;

// PDF formats
export const SUPPORTED_PDF_FORMATS = {
  'application/pdf': ['.pdf'],
} as const;

// All supported file formats
export const SUPPORTED_FILE_FORMATS = {
  ...SUPPORTED_IMAGE_FORMATS,
  ...SUPPORTED_PDF_FORMATS,
} as const;

export const SUPPORTED_EXTENSIONS = Object.values(SUPPORTED_FILE_FORMATS).flat();
export { SUPPORTED_FILE_ACCEPT, SUPPORTED_FILE_TYPES, SUPPORTED_MIME_TYPES };

/**
 * Processed file attachment with all metadata calculated
 */
export type ProcessedFileAttachment = {
  id: string;
  name: string;
  contentType: string;
  url: string;
  isScreenshot: false;
  file: File;
  fileSizeMB: number;
  estimatedTokens: number;
};

export type FileUploadResult =
  | {
      success: true;
      file: File;
      dataUrl: string;
      attachment: ProcessedFileAttachment;
    }
  | {
      success: false;
      error: string;
    };

/**
 * Validate if a file is a supported format (images and PDFs)
 * Uses the shared validation function from @ctrl-enter/tools
 * Note: PDFs will have their text extracted and sent to AI as text
 */
export const validateFile = (file: File): { valid: boolean; error?: string } => {
  const result = sharedValidateFile(file, SUPPORTED_FILE_TYPES, ATTACHMENT_LIMITS.MAX_FILE_SIZE);

  // Add informational message for PDF files about text extraction
  if (result.valid && file.type === 'application/pdf') {
    return {
      valid: true,
      // This is not actually an error, just informational
      error: 'PDF uploaded! Text will be extracted and sent to AI for analysis.',
    };
  }

  return result;
};

/**
 * Validate if a file is a supported image format
 * Now uses the shared validation function from @ctrl-enter/tools
 * @deprecated Use validateFile for mixed file type support
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  return sharedValidateImageFile(file, SUPPORTED_MIME_TYPES, ATTACHMENT_LIMITS.MAX_FILE_SIZE);
};

/**
 * Smart validation that prioritizes new files and suggests dropping old topic files
 */
export const validateAndOptimizeRequestSize = (
  newFiles: File[],
  existingAttachments: Array<{ url: string }> = [],
  existingTopicFiles: Array<{ base64: string; id: string; uploadedAt: number }> = []
): {
  canProceed: boolean;
  optimizedTopicFiles?: Array<{ base64: string; id: string; uploadedAt: number }>;
  error?: string;
  details: {
    currentSizeKB: number;
    newFilesSizeKB: number;
    totalSizeKB: number;
    optimizedSizeKB?: number;
    limitKB: number;
  };
} => {
  const limitKB = ATTACHMENT_LIMITS.MAX_TOTAL_REQUEST_SIZE_KB;

  // Calculate sizes
  const existingAttachmentsSize = calculateAttachmentsSize(existingAttachments);
  const existingTopicFilesSize = existingTopicFiles.reduce((total, file) => {
    return total + getBase64SizeKB(file.base64);
  }, 0);
  const currentSizeKB = existingAttachmentsSize + existingTopicFilesSize;

  // Calculate new files size (estimate based on file size)
  const newFilesSizeKB = newFiles.reduce((total, file) => {
    return total + Math.round((file.size * 1.33) / 1024);
  }, 0);

  const totalSizeKB = currentSizeKB + newFilesSizeKB;

  // Check if new files alone exceed limits (hard block)
  const draftOnlySizeKB = existingAttachmentsSize + newFilesSizeKB;
  if (draftOnlySizeKB > limitKB) {
    return {
      canProceed: false,
      error: `New files (${(newFilesSizeKB / 1024).toFixed(1)}MB) plus current draft (${(existingAttachmentsSize / 1024).toFixed(1)}MB) exceed the ${(limitKB / 1024).toFixed(1)}MB limit. Remove some draft files first.`,
      details: {
        currentSizeKB,
        newFilesSizeKB,
        totalSizeKB,
        limitKB,
      },
    };
  }

  // Check individual file count limits (hard block)
  if (newFiles.length > ATTACHMENT_LIMITS.MAX_NEW_ATTACHMENTS_PER_REQUEST) {
    return {
      canProceed: false,
      error: `Cannot add ${newFiles.length} files at once. Maximum ${ATTACHMENT_LIMITS.MAX_NEW_ATTACHMENTS_PER_REQUEST} new files allowed per upload`,
      details: {
        currentSizeKB,
        newFilesSizeKB,
        totalSizeKB,
        limitKB,
      },
    };
  }

  // If total size is within limits, proceed without dropping anything
  if (totalSizeKB <= limitKB) {
    const totalAttachments =
      existingAttachments.length + existingTopicFiles.length + newFiles.length;
    if (totalAttachments <= ATTACHMENT_LIMITS.MAX_ATTACHMENTS_PER_REQUEST) {
      return {
        canProceed: true,
        details: {
          currentSizeKB,
          newFilesSizeKB,
          totalSizeKB,
          limitKB,
        },
      };
    }
  }

  // Need to optimize: silently drop oldest topic files to make room
  const availableSpaceKB = limitKB - draftOnlySizeKB; // Space available after draft + new files
  const sortedTopicFiles = [...existingTopicFiles].sort((a, b) => a.uploadedAt - b.uploadedAt); // Oldest first

  let remainingSpaceKB = availableSpaceKB;
  const optimizedTopicFiles: Array<{ base64: string; id: string; uploadedAt: number }> = [];

  // Keep as many topic files as possible, starting with newest (silently drop others)
  for (let i = sortedTopicFiles.length - 1; i >= 0; i--) {
    const file = sortedTopicFiles[i];
    const fileSizeKB = getBase64SizeKB(file?.base64 ?? '');

    if (
      remainingSpaceKB >= fileSizeKB &&
      existingAttachments.length + optimizedTopicFiles.length + newFiles.length <
        ATTACHMENT_LIMITS.MAX_ATTACHMENTS_PER_REQUEST &&
      file
    ) {
      optimizedTopicFiles.unshift(file); // Add to beginning to maintain order
      remainingSpaceKB -= fileSizeKB;
    }
    // Silently drop files that don't fit - no notification needed
  }

  const optimizedSizeKB =
    draftOnlySizeKB +
    optimizedTopicFiles.reduce((total, file) => {
      return total + getBase64SizeKB(file?.base64 ?? '');
    }, 0);

  return {
    canProceed: true,
    optimizedTopicFiles,
    details: {
      currentSizeKB,
      newFilesSizeKB,
      totalSizeKB,
      optimizedSizeKB,
      limitKB,
    },
  };
};

/**
 * Legacy validation function for backward compatibility
 * @deprecated Use validateAndOptimizeRequestSize instead
 */
export const validateRequestSize = (
  newFiles: File[],
  existingAttachments: Array<{ url: string }> = [],
  existingTopicFiles: Array<{ base64: string }> = []
): {
  valid: boolean;
  error?: string;
  details?: {
    currentSizeKB: number;
    newFilesSizeKB: number;
    totalSizeKB: number;
    limitKB: number;
  };
} => {
  const result = validateAndOptimizeRequestSize(
    newFiles,
    existingAttachments,
    existingTopicFiles.map((file, index) => ({
      ...file,
      id: `legacy-${index}`,
      uploadedAt: Date.now(),
    }))
  );

  return {
    valid: result.canProceed,
    error: result.error!,
    details: result.details,
  };
};

/**
 * Convert a File to a base64 data URL
 */
export const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read file as data URL'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Process an uploaded file and convert it to an attachment format (supports images and PDFs)
 */
export const processFile = async (file: File): Promise<FileUploadResult> => {
  try {
    // Validate the file
    const validation = validateFile(file);
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error!,
      };
    }

    // Convert to data URL
    const dataUrl = await fileToDataUrl(file);

    // Create attachment object with UUID and all metadata
    const attachment: ProcessedFileAttachment = {
      id: randomUUID(),
      name: file.name,
      contentType: file.type,
      url: dataUrl,
      isScreenshot: false,
      file, // Store original File object for token estimation
      fileSizeMB: file.size / (1024 * 1024), // Convert bytes to MB
      estimatedTokens: 0, // Will calculate below
    };

    // Calculate estimated tokens using the utility
    attachment.estimatedTokens = estimateAttachmentTokens(attachment as DraftAttachment);

    return {
      success: true,
      file,
      dataUrl,
      attachment,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to process file',
    };
  }
};

/**
 * Process an uploaded image file and convert it to an attachment format
 * @deprecated Use processFile for mixed file type support
 */
export const processImageFile = async (file: File): Promise<FileUploadResult> => {
  return processFile(file);
};

/**
 * Open file picker dialog for file selection (images and PDFs)
 * Uses both MIME types and file extensions for maximum browser compatibility
 */
export const openFilePicker = (): Promise<File[]> => {
  return new Promise((resolve, reject) => {
    try {
      // Create file input element
      const input = document.createElement('input');
      input.type = 'file';
      // Use SUPPORTED_FILE_ACCEPT which includes both MIME types and extensions
      // This ensures PDFs and images are selectable on all browsers/systems
      input.accept = SUPPORTED_FILE_ACCEPT.join(',');
      // Allow shift/cmd-click multi-select; the chat view caps the total
      // at ATTACHMENT_LIMITS.MAX_NEW_ATTACHMENTS_PER_REQUEST and surfaces a toast when exceeded.
      input.multiple = true;

      // Handle file selection
      input.onchange = event => {
        const target = event.target as HTMLInputElement;
        const files = target.files;

        if (files && files.length > 0) {
          resolve(Array.from(files));
        } else {
          resolve([]);
        }

        // Clean up
        document.body.removeChild(input);
      };

      // Handle cancellation
      input.oncancel = () => {
        resolve([]);
        document.body.removeChild(input);
      };

      // Add to DOM and trigger click
      input.style.display = 'none';
      document.body.appendChild(input);
      input.click();
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Open file picker dialog for image selection
 * @deprecated Use openFilePicker for mixed file type support
 */
export const openImageFilePicker = (): Promise<File[]> => {
  return new Promise((resolve, reject) => {
    try {
      // Create file input element
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = SUPPORTED_MIME_TYPES.join(',');
      input.multiple = true;

      // Handle file selection
      input.onchange = event => {
        const target = event.target as HTMLInputElement;
        const files = target.files;

        if (files && files.length > 0) {
          resolve(Array.from(files));
        } else {
          resolve([]);
        }

        // Clean up
        document.body.removeChild(input);
      };

      // Handle cancellation
      input.oncancel = () => {
        resolve([]);
        document.body.removeChild(input);
      };

      // Add to DOM and trigger click
      input.style.display = 'none';
      document.body.appendChild(input);
      input.click();
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Handle multiple file uploads with smart size optimization (supports images and PDFs)
 */
export const handleFileUpload = async (
  existingAttachments: Array<{ url: string }> = [],
  existingTopicFiles: Array<{ base64: string; id: string; uploadedAt: number }> = []
): Promise<{
  attachments: ProcessedFileAttachment[];
  optimizedTopicFiles?: Array<{ base64: string; id: string; uploadedAt: number }>;
  errors: string[];
  sizeValidation?: {
    currentSizeKB: number;
    newFilesSizeKB: number;
    totalSizeKB: number;
    optimizedSizeKB?: number;
    limitKB: number;
  };
}> => {
  try {
    const files = await openFilePicker();

    if (files.length === 0) {
      return { attachments: [], errors: [] };
    }

    return processSelectedFiles(files, existingAttachments, existingTopicFiles);
  } catch (error) {
    return {
      attachments: [],
      errors: [error instanceof Error ? error.message : 'Failed to upload files'],
    };
  }
};

/**
 * Handle multiple file uploads with smart size optimization
 * @deprecated Use handleFileUpload for mixed file type support
 */
export const handleImageUpload = async (
  existingAttachments: Array<{ url: string }> = [],
  existingTopicFiles: Array<{ base64: string; id: string; uploadedAt: number }> = []
): Promise<{
  attachments: ProcessedFileAttachment[];
  optimizedTopicFiles?: Array<{ base64: string; id: string; uploadedAt: number }>;
  errors: string[];
  sizeValidation?: {
    currentSizeKB: number;
    newFilesSizeKB: number;
    totalSizeKB: number;
    optimizedSizeKB?: number;
    limitKB: number;
  };
}> => {
  try {
    const files = await openImageFilePicker();

    if (files.length === 0) {
      return { attachments: [], errors: [] };
    }

    return processSelectedFiles(files, existingAttachments, existingTopicFiles);
  } catch (error) {
    return {
      attachments: [],
      errors: [error instanceof Error ? error.message : 'Failed to upload files'],
    };
  }
};

/**
 * Process a provided list of files (e.g., from drag-and-drop) with the same
 * validation and transformation logic used by the picker flow.
 */
export const processSelectedFiles = async (
  files: File[],
  existingAttachments: Array<{ url: string }> = [],
  existingTopicFiles: Array<{ base64: string; id: string; uploadedAt: number }> = []
): Promise<{
  attachments: ProcessedFileAttachment[];
  optimizedTopicFiles?: Array<{ base64: string; id: string; uploadedAt: number }>;
  errors: string[];
  sizeValidation?: {
    currentSizeKB: number;
    newFilesSizeKB: number;
    totalSizeKB: number;
    optimizedSizeKB?: number;
    limitKB: number;
  };
}> => {
  if (!files || files.length === 0) {
    return { attachments: [], errors: [] };
  }

  // Use smart validation that can optimize topic files
  const validation = validateAndOptimizeRequestSize(files, existingAttachments, existingTopicFiles);

  if (!validation.canProceed) {
    return {
      attachments: [],
      errors: [validation.error!],
      sizeValidation: validation.details,
    };
  }

  // Process files - validation already passed
  const results = await Promise.all(files.map(file => processFile(file)));

  const attachments: ProcessedFileAttachment[] = [];
  const errors: string[] = [];

  results.forEach((result, index) => {
    if (result.success) {
      attachments.push(result.attachment);
    } else {
      errors.push(`${files[index]?.name}: ${result.error}`);
    }
  });

  return {
    attachments,
    ...(validation.optimizedTopicFiles && {
      optimizedTopicFiles: validation.optimizedTopicFiles,
    }),
    errors,
    sizeValidation: validation.details,
  };
};
